import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { bookingAdminApi } from "@/lib/callables";
import {
  findBookingPharmacy,
  findAllBookingPharmacies,
  getBookingRooms,
  getPharmacistAvailabilities,
  type PharmacistAvailability,
} from "@/lib/bookingData";
import {
  DAY_NAMES,
  type BookingPharmacy,
  type BookingRoom,
} from "@/lib/bookingTypes";
import { useToast } from "@/lib/toast";

const BOOKING_BASE = "https://book-consultation.katana-healthcare.com/";

export function BookingSettingsSection({
  chainId,
  pharmacyId,
}: {
  chainId: string;
  pharmacyId: string;
}) {
  const qc = useQueryClient();
  const queryKey = ["booking", chainId, pharmacyId];

  const bookingQ = useQuery({
    queryKey,
    queryFn: () => findBookingPharmacy(chainId, pharmacyId),
    enabled: !!chainId && !!pharmacyId,
  });

  // Every live booking record for this CCS pharmacy, canonical first. These
  // are DUPLICATES of one pharmacy, not separate locations — we no longer
  // emit a snippet per record (that handed admins ids pointing at empty
  // availability). Used only to detect and warn about unreconciled data.
  const allLocationsQ = useQuery({
    queryKey: [...queryKey, "all-locations"],
    queryFn: () => findAllBookingPharmacies(chainId, pharmacyId),
    enabled: !!chainId && !!pharmacyId,
  });

  const roomsQ = useQuery({
    queryKey: [...queryKey, "rooms"],
    queryFn: () => getBookingRooms(bookingQ.data!.pharmacy.id),
    enabled: !!bookingQ.data,
  });

  const availabilityQ = useQuery({
    queryKey: [...queryKey, "availability"],
    queryFn: () => getPharmacistAvailabilities(bookingQ.data!.pharmacy.id),
    enabled: !!bookingQ.data,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey });
  };

  return (
    <Paper>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Booking Settings
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {bookingQ.isLoading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={20} />
          </Box>
        ) : bookingQ.error ? (
          <Alert severity="error">
            {(bookingQ.error as Error).message}
          </Alert>
        ) : !bookingQ.data ? (
          <Alert severity="info">
            This pharmacy hasn't set up online booking yet. Ask the pharmacist
            to complete "Booking Setup" once in the CCS Pharmacy app — once
            they do, its booking page, rooms, and availability will appear
            here for you to manage.
          </Alert>
        ) : (
          <Stack spacing={3}>
            {bookingQ.data.ambiguous && (
              <Alert severity="warning">
                This pharmacy has more than one booking record
                {(allLocationsQ.data?.length ?? 0) > 1
                  ? ` (${allLocationsQ.data!.length})`
                  : ""}
                . Everything below — including the embed snippet — uses the
                canonical record, which is the same one the pharmacist's app
                writes availability to, so the booking link works. The extra
                records are leftovers and should be merged by support
                (booking-record reconciliation) to avoid confusion.
              </Alert>
            )}
            {!availabilityQ.isLoading && (availabilityQ.data?.length ?? 0) === 0 && (
              <Alert severity="warning">
                No pharmacist has set an availability schedule on this
                pharmacy's booking record yet, so the patient booking link
                will show no times. Ask a pharmacist to set their availability
                in the CCS Pharmacy app's Bookings tab.
              </Alert>
            )}
            <BrandingForm
              chainId={chainId}
              pharmacyId={pharmacyId}
              initial={bookingQ.data.pharmacy}
              onSaved={invalidateAll}
            />
            <Divider />
            <EmbedSnippetSection pharmacy={bookingQ.data.pharmacy} />
            <Divider />
            <RoomsSection
              rooms={roomsQ.data ?? []}
              loading={roomsQ.isLoading}
            />
            <Divider />
            <PharmacistAvailabilitySection
              availabilities={availabilityQ.data ?? []}
              loading={availabilityQ.isLoading}
            />
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

function BrandingForm({
  chainId,
  pharmacyId,
  initial,
  onSaved,
}: {
  chainId: string;
  pharmacyId: string;
  initial: { name: string; address: string; eircode: string; primaryColor?: string; logoUrl?: string };
  onSaved: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [name, setName] = useState(initial.name);
  const [address, setAddress] = useState(initial.address);
  const [eircode, setEircode] = useState(initial.eircode);
  const [primaryColor, setPrimaryColor] = useState(initial.primaryColor ?? "");
  const [logoUrl, setLogoUrl] = useState(initial.logoUrl ?? "");

  const m = useMutation({
    mutationFn: () =>
      bookingAdminApi.updatePharmacy({
        chainId,
        pharmacyId,
        name: name.trim(),
        address: address.trim(),
        eircode: eircode.trim(),
        primaryColor: primaryColor.trim() || undefined,
        logoUrl: logoUrl.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess("Booking page updated.");
      onSaved();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed to save"),
  });

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Booking page &amp; branding
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <TextField
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Eircode"
          helperText="e.g. D02 XY12"
          value={eircode}
          onChange={(e) => setEircode(e.target.value)}
          size="small"
          fullWidth
        />
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <TextField
            label="Primary color"
            helperText="Hex color, e.g. #007AFF"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            size="small"
            fullWidth
          />
          {primaryColor.match(/^#[0-9a-fA-F]{6}$/) && (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: primaryColor,
                border: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
                mt: 0.25,
              }}
            />
          )}
        </Box>
        <TextField
          label="Logo URL"
          helperText="https:// link to the logo image"
          value={logoUrl}
          onChange={(e) => setLogoUrl(e.target.value)}
          size="small"
          fullWidth
          sx={{ gridColumn: { md: "span 2" } }}
        />
      </Box>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={() => m.mutate()}
          disabled={!name.trim() || !address.trim() || !eircode.trim() || m.isPending}
        >
          {m.isPending ? "Saving…" : "Save booking page"}
        </Button>
      </Box>
    </Box>
  );
}

function EmbedSnippetSection({ pharmacy }: { pharmacy: BookingPharmacy }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Embed snippet
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Paste this snippet into this pharmacy's page on your chain website. It is pre-loaded
        with the pharmacy's canonical booking ID and branding — the same record the
        pharmacist's app writes availability to.
      </Typography>
      <LocationSnippet pharmacy={pharmacy} showLabel={false} />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
        Add <code>data-container-id="your-div-id"</code> to embed the booking form inline instead of as a popup button.
      </Typography>
    </Box>
  );
}

function LocationSnippet({
  pharmacy,
  showLabel,
}: {
  pharmacy: BookingPharmacy;
  showLabel: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const color = pharmacy.primaryColor?.match(/^#[0-9a-fA-F]{6}$/)
    ? pharmacy.primaryColor
    : "#007AFF";

  const snippet =
    `<script\n` +
    `  src="${BOOKING_BASE}widget.js"\n` +
    `  data-pharmacy-id="${pharmacy.id}"\n` +
    `  data-primary-color="${color}"\n` +
    `></script>`;

  const handleCopy = () => {
    void navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box>
      {showLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 500 }}>
          {pharmacy.name}{pharmacy.eircode ? ` · ${pharmacy.eircode}` : ""}
        </Typography>
      )}
      <Box
        sx={{
          position: "relative",
          bgcolor: "grey.900",
          borderRadius: 1,
          p: 2,
          pr: 6,
        }}
      >
        <Typography
          component="pre"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.78rem",
            color: "grey.100",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            m: 0,
          }}
        >
          {snippet}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: copied ? "success.light" : "grey.400",
              "&:hover": { color: "grey.100" },
            }}
          >
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function RoomsSection({
  rooms,
  loading,
}: {
  rooms: BookingRoom[];
  loading: boolean;
}) {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Booking rooms
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Rooms are managed by the pharmacist from the CCS Pharmacy mobile app. Each active room is a concurrent booking slot.
      </Alert>
      {loading ? (
        <Box sx={{ textAlign: "center", py: 1 }}>
          <CircularProgress size={16} />
        </Box>
      ) : rooms.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No rooms set up yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {rooms.map((room) => (
            <Stack
              key={room.id}
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  flexGrow: 1,
                  textDecoration: room.isActive ? "none" : "line-through",
                  color: room.isActive ? "text.primary" : "text.secondary",
                }}
              >
                {room.name}
              </Typography>
              <Chip
                label={room.isActive ? "Active" : "Inactive"}
                size="small"
                color={room.isActive ? "success" : "default"}
              />
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function PharmacistAvailabilitySection({
  availabilities,
  loading,
}: {
  availabilities: PharmacistAvailability[];
  loading: boolean;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Pharmacist availability
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Each pharmacist's weekly schedule is set from the CCS Pharmacy mobile app. The booking page shows slots from the union of all schedules below.
      </Alert>
      {loading ? (
        <Box sx={{ textAlign: "center", py: 1 }}>
          <CircularProgress size={16} />
        </Box>
      ) : availabilities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No pharmacist has set up their availability schedule yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {availabilities.map((avail) => (
            <Box
              key={avail.pharmacistId}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "center", px: 1.5, py: 1 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
                  Pharmacist <code style={{ fontSize: "0.75rem" }}>{avail.pharmacistId.slice(0, 8)}…</code>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {avail.windows.length} window{avail.windows.length !== 1 ? "s" : ""}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setExpandedId((cur) => (cur === avail.pharmacistId ? null : avail.pharmacistId))}
                >
                  {expandedId === avail.pharmacistId ? "Hide" : "View schedule"}
                </Button>
              </Stack>
              {expandedId === avail.pharmacistId && (
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                        Weekly schedule
                      </Typography>
                      {avail.windows.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No availability windows — no slots will be generated.
                        </Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          {avail.windows.map((w, i) => (
                            <Typography key={i} variant="body2">
                              {DAY_NAMES[w.dayOfWeek]}: {w.startTime} – {w.endTime}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Box>
                    {avail.blockoutDates.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                          Blockout dates
                        </Typography>
                        <Stack spacing={0.5}>
                          {avail.blockoutDates.map((b, i) => (
                            <Typography key={i} variant="body2">
                              {b.date}{b.reason ? ` — ${b.reason}` : ""}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
